import { createResource, type Accessor } from 'solid-js'
import { backendAxios } from '../../axios'
import type { MapRowValue } from '../../backend.d'
import { getStateDiskByType } from '../../lib/core/disk/stateDiskResolver'
import {
  EXPORT_DISK_PLACEHOLDER_ERROR,
  StateMapDisk,
} from '../../lib/core/state/state_map_disk.enum'
import { StateType } from '../../lib/core/state/state_type.enum'
import { hasIndexedDB } from '../../lib/core/Storage/IndexedDB/hasIndexedDB'
import { Nullable } from '../../typescript'
import { loadWorkspaceMapRow } from '../../lib/core/workspace/workspaceMapsClient'
import parseIntOrNull from '../../lib/core/util/math/parseIntOrNull'
import { decodePersistedStateData } from '../../lib/core/state/persisted_state'

export type MapResourceRowValue = MapRowValue & {
  file_id?: string
}

export async function fetchMapById(
  stateId: Nullable<number | string>,
  disk: StateMapDisk
): Promise<MapResourceRowValue | null> {
  if (stateId) {
    const numericStateId = typeof stateId === 'number' ? stateId : parseIntOrNull(stateId)
    const useIndexedDB = disk === StateMapDisk.IndexedDB
    const useMemory = disk === StateMapDisk.Memory
    const useCloud = disk === StateMapDisk.Cloud
    const preferredLocalDisk = useMemory ? StateMapDisk.Memory : useIndexedDB ? StateMapDisk.IndexedDB : undefined
    if (disk === StateMapDisk.Export) {
      throw new Error(EXPORT_DISK_PLACEHOLDER_ERROR)
    }
    if (disk === StateMapDisk.Workspace) {
      const workspaceFileId = String(stateId)
      return await loadWorkspaceMapRow(workspaceFileId)
    }
    if (!useIndexedDB && !useMemory && !useCloud) {
      throw new Error('Invalid disk: ' + disk)
    }
    if (useIndexedDB && !hasIndexedDB()) {
      throw new Error('IndexedDB requested but not available')
    }

    if (useIndexedDB || useMemory) {
      if (!numericStateId) {
        return null
      }
      const row = await getStateDiskByType(disk, StateType.Map).load(numericStateId)
      if (row) {
        return {
          ...row,
          data: decodePersistedStateData(row.data, preferredLocalDisk),
        } as MapResourceRowValue
      }
      return null
    }

    // Otherwise fetch from cloud backend
    if (!numericStateId) {
      return null
    }
    const response = await backendAxios.get(`/st1/maps/${numericStateId}`)
    const row: MapRowValue = response.data
    return {
      ...row,
      data: decodePersistedStateData(row.data, preferredLocalDisk),
    } as MapResourceRowValue
  }
  return null
}

export default function createMapRowResource(
  stateId: Accessor<Nullable<number | string>>,
  disk: Accessor<StateMapDisk>
) {
  const resource = createResource(stateId, (id) => fetchMapById(id, disk()))
  return resource
}
